import { RootStore } from 'store/rootStore';
import { makeAutoObservable, runInAction } from 'mobx';
import { SEUser } from 'types/userService';
import { Subscriber, EmployeeInvitationStatusTypes, SubscriberSERoles } from 'types/electionService';
import { inviteToSelfEnroll } from 'api/operations/userService';
import { getSubscribers } from 'api/operations/electionService';

export default class InviteModalStore {
  rootStore: RootStore;
  private _loading = false;
  private _subscribers: Subscriber[] = [];
  private _groupId: string | null = null;
  private _periodId = '';
  // The value is needed to show the warning modal only if someone has been invited
  private _isAnybodyInvited = false;

  public isInviteModalOpen = false;
  public isWarningModalOpen = false;

  public get subscribers(): Subscriber[] {
    return this._subscribers;
  }

  public get loading(): boolean {
    return this._loading;
  }

  public get showRemindInvitationBtn(): boolean {
    return this.subscribers.some(
      ({ selfEnrollStatus }) => selfEnrollStatus === EmployeeInvitationStatusTypes.REGISTERED,
    );
  }

  public get isInviteAllBtnDisabled(): boolean {
    return (
      this.subscribers.every(
        ({ selfEnrollStatus }) => selfEnrollStatus !== EmployeeInvitationStatusTypes.NOT_YET_INVITED,
      ) || this.areAllSubscribersWithoutEmail
    );
  }

  public get areAllSubscribersWithoutEmail(): boolean {
    return this.subscribers.every(
      ({ selfEnrollStatus }) => selfEnrollStatus === EmployeeInvitationStatusTypes.ADD_MAIL,
    );
  }

  private _setLoading(loadingStatus: boolean): void {
    this._loading = loadingStatus;
  }

  public openInviteModal(periodId: string): void {
    this._periodId = periodId;
    void this._setSubscribers(this.rootStore.employeeElectionsStore.subscribers);
    this.isInviteModalOpen = true;
  }

  private _openWarningModal(): void {
    this.isWarningModalOpen = true;
  }

  public closeWarningModal(): void {
    this._setLoading(true);
    // Timeout is needed to wait until the list of subscribers will be updated
    setTimeout(async (): Promise<void> => {
      const { periodId, sortBy, sortOrder, filter, currentPage, searchValue, classesFilter, statusesFilter } =
        this.rootStore.employeeElectionsStore;
      const subscribersResult = await getSubscribers(
        this._groupId || '',
        periodId,
        sortBy,
        sortOrder,
        filter,
        searchValue,
        statusesFilter,
        classesFilter,
        currentPage,
      );
      const updatedSubscribersList = subscribersResult.result;
      // update all subscribers cards
      this.rootStore.employeeElectionsStore.subscribers = updatedSubscribersList;
      this._subscribers = updatedSubscribersList;
      if (!this._groupId) {
        throw new Error('Group ID is required');
      }
      await this.rootStore.employeeElectionsStore.getStatistic(this._groupId, periodId, false);
      this.isWarningModalOpen = false;
    }, 2000);
    this._setLoading(false);
    this.isWarningModalOpen = false;
  }

  private async _setSubscribers(subscribers: Subscriber[]): Promise<void> {
    this._setLoading(true);
    const selectedSubscribers = subscribers.filter(({ id }) =>
      this.rootStore.employeeElectionsStore.selectedSubscribersIds.includes(id),
    );

    // Get all invited subscribers
    const invitedSubscribersResult = await this.rootStore.electionStore.getInvitedSubscribers(this._periodId);
    runInAction(() => {
      // Show only invited subscribers with Employee role
      const invitedSubscribers = invitedSubscribersResult.data.getInvitedSubscribers.filter(
        ({ subSERole }) => subSERole !== SubscriberSERoles.EMPLOYER,
      );
      const updatedSubscribers: Subscriber[] = [...invitedSubscribers];

      selectedSubscribers.forEach((selectedSub) => {
        const selectedAndInvitedSub = invitedSubscribers.find((invitedSub) => invitedSub.id === selectedSub.id);
        if (!selectedAndInvitedSub) updatedSubscribers.push(selectedSub);
      });

      this._subscribers = updatedSubscribers;
      this._setLoading(false);
    });
  }

  public setGroupId(groupId: string): void {
    if (groupId) this._groupId = groupId;
  }

  public closeInviteModal(): void {
    this._setLoading(true);
    this.isInviteModalOpen = false;
    // Timeout is needed to wait until the list of subscribers will be updated
    if (this._isAnybodyInvited) {
      setTimeout(() => {
        this._openWarningModal();
        this._isAnybodyInvited = false;
        this._subscribers = [];
      }, 1000);
    }

    this._setLoading(false);
  }

  // eslint-disable-next-line class-methods-use-this
  private _createInitialUserForInvite(subscriber: Subscriber, groupId: string): SEUser {
    const {
      id,
      email,
      subSERole,
      common: { firstName, lastName, middleName },
    } = subscriber;

    return {
      firstName,
      middleName: middleName || '',
      lastName,
      email: email || '',
      userType: subSERole,
      groupId,
      periodId: this._periodId,
      subscriberId: id,
    };
  }

  public async inviteSingleUser(singleSubscriberId: string): Promise<void> {
    this._isAnybodyInvited = true;
    const currentSubscriber = this.subscribers.find(({ id }) => id === singleSubscriberId);

    if (currentSubscriber && this._groupId) {
      this._setLoading(true);
      const newSubscribers = await inviteToSelfEnroll([
        this._createInitialUserForInvite(currentSubscriber, this._groupId),
      ]);

      // If the subscriber is successfully invited
      if (newSubscribers.length) {
        // for visually display the changed status without delay
        const updatedSubscribers = this.subscribers.map((subscriber): Subscriber => {
          if (
            subscriber.id === singleSubscriberId &&
            subscriber.selfEnrollStatus === EmployeeInvitationStatusTypes.NOT_YET_INVITED
          ) {
            return { ...subscriber, selfEnrollStatus: 'INVITED' as EmployeeInvitationStatusTypes };
          }
          return subscriber;
        });
        this._subscribers = updatedSubscribers;
      }

      this._setLoading(false);
    }
  }

  public async inviteUsers(): Promise<void> {
    this._isAnybodyInvited = true;

    if (this._groupId) {
      const convertedInvitedUsers = this.subscribers
        .filter(
          ({ selfEnrollStatus, subSERole }) =>
            selfEnrollStatus === EmployeeInvitationStatusTypes.NOT_YET_INVITED &&
            subSERole !== SubscriberSERoles.EMPLOYER,
        )
        .map((subscriber) => this._createInitialUserForInvite(subscriber, this._groupId || ''));

      this._setLoading(true);
      const newSubscribers = await inviteToSelfEnroll(convertedInvitedUsers);

      // If the subscribers are successfully invited
      if (newSubscribers.length) {
        // for visually display the changed statuses without delay
        const updatedSubscribers = this.subscribers.map((subscriber): Subscriber => {
          if (subscriber.selfEnrollStatus === EmployeeInvitationStatusTypes.NOT_YET_INVITED) {
            return { ...subscriber, selfEnrollStatus: 'INVITED' as EmployeeInvitationStatusTypes };
          }
          return subscriber;
        });
        this._subscribers = updatedSubscribers;
      }

      this._setLoading(false);
      this.closeInviteModal();
    }
  }

  public async remindSingleRegUserAboutInvitation(singleSubscriberId: string): Promise<void> {
    this._setLoading(true);
    await this.rootStore.electionStore.remindFinishSE(this._periodId, [singleSubscriberId]);
    this._setLoading(false);
  }

  public async remindRegUsersAboutInvitation(): Promise<void> {
    this._setLoading(true);
    const convertedRegisteredUsersIds = this.subscribers
      .filter(
        ({ selfEnrollStatus, subSERole }) =>
          selfEnrollStatus === EmployeeInvitationStatusTypes.REGISTERED && subSERole !== SubscriberSERoles.EMPLOYER,
      )
      .map((sub) => sub.id);
    await this.rootStore.electionStore.remindFinishSE(this._periodId, convertedRegisteredUsersIds);
    this._setLoading(false);
    this.closeInviteModal();
  }

  // Remove only from list in the invitation table (if subscriber doesn't need to be invited this time)
  public unselectSubscriber(subscriberId: string): void {
    this._subscribers = this._subscribers.filter(({ id }) => id !== subscriberId);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this._subscribers = [];

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
